import {clinchEnums} from '@cl/shared';
import {CLEnumKeys, CLEnumType} from 'common/src/modules/enums/enums.types';
import {ProductSelectionStrategyType} from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.types';
import isFlagEnabled from 'common/src/modules/featureFlags/utils/isFlagEnabled';
import {FeatureFlagKey} from 'common/src/modules/featureFlags/featureFlags.types';
import {CampaignType} from 'common/src/modules/campaigns/campaigns.types';

export enum FeedType {
  Product = 'Product',
  Location = 'Location',
  Content = 'Content',
}

export type Strategy = {
  strategyId: number;
  name: string;
  description: string;
};

export default function getStrategies(feedType: FeedType, campaignType: CampaignType): Strategy[] {
  let strategies: Strategy[] = [];

  if (feedType === FeedType.Product) {
    const productStrategies =
      clinchEnums.loadedEnumsSubject.data[CLEnumType.CampaignPage]?.[
        CLEnumKeys.CampaignPage.CampaignContentType
      ]?.enumsForSelector ?? [];

    const enableProductsByDSP = isFlagEnabled(FeatureFlagKey.EnableProductsByDSPStrategy);
    const enableItemPerCategory = isFlagEnabled(FeatureFlagKey.EnableItemPerCategory);
    const isViewedItemsStrategyEnabled = isFlagEnabled(FeatureFlagKey.EnableViewedItemsStrategy);

    strategies = productStrategies
      .filter((strategy) => {
        if (
          !enableProductsByDSP &&
          +strategy.value === ProductSelectionStrategyType.ProductsFromDsp
        ) {
          return false;
        }

        if (
          !enableItemPerCategory &&
          +strategy.value === ProductSelectionStrategyType.ItemPerCategory
        ) {
          return false;
        }

        if (
          !isViewedItemsStrategyEnabled &&
          +strategy.value === ProductSelectionStrategyType.ViewedItems
        ) {
          return false;
        }

        const campaignTypes = strategy.campaignTypes ?? [];

        if (campaignTypes.length > 0 && !campaignTypes.includes(campaignType)) {
          return false;
        }

        return true;
      })
      .map((strategy) => ({
        strategyId: Number(strategy.value),
        name: strategy.uiName,
        description: strategy.description ?? '',
      }));
  }

  if (feedType === FeedType.Location) {
    strategies =
      clinchEnums.loadedEnumsSubject.data[CLEnumType.CampaignPage]?.[
        CLEnumKeys.CampaignPage.LocationTargetingMethod
      ]?.enumsForSelector.map((enumValue) => ({
        strategyId: Number(enumValue.value),
        name: enumValue.uiName,
        description: enumValue.description ?? '',
      })) ?? [];
  }

  if (feedType === FeedType.Content) {
    strategies =
      clinchEnums.loadedEnumsSubject.data[CLEnumType.CampaignPage]?.[
        CLEnumKeys.CampaignPage.ContentSelectionStrategy
      ]?.enumsForSelector.map((enumValue) => ({
        strategyId: Number(enumValue.value),
        name: enumValue.uiName,
        description: enumValue.description ?? '',
      })) ?? [];
  }

  return strategies;
}
